import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, TrendingUp, Calendar, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { fmtINR } from "@/lib/financial";
import ClientSidebar from "@/components/ClientSidebar";
import { fireConfetti } from "@/lib/gamification";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

// Mirrors the Wealth Accelerator master Excel
const ASSET_SECTIONS = [
  { key: "lifestyle", label: "Lifestyle Assets", items: [
    ["own_house", "Own House"], ["vacation_house", "Vacation House"],
    ["vehicles", "Vehicles"], ["gold_jewellery", "Gold Jewellery"],
    ["recreational_equipment", "Recreational Equipment"],
    ["household_furnishings", "Household Furnishings"],
    ["collectibles", "Collectibles (art / coins / stamps)"],
  ]},
  { key: "financial", label: "Financial Assets", items: [
    ["savings_bank", "Savings Bank"], ["current_account", "Current Account"],
    ["fixed_deposits", "Fixed Deposits"],
    ["epf_self", "EPF (Self)"], ["epf_spouse", "EPF (Spouse)"],
    ["ppf", "PPF"], ["nps", "NPS"],
    ["direct_equity", "Direct Equity"], ["esops_rsus", "ESOPs / RSUs"],
    ["mutual_funds_equity", "Mutual Funds (Equity)"],
    ["mutual_funds_debt", "Mutual Funds (Debt)"],
    ["bonds_debentures", "Bonds / Debentures"],
    ["pomis_nsc_kvp", "POMIS / NSC / KVP"],
    ["sovereign_gold_bonds", "Sovereign Gold Bonds"],
    ["gold_coins_bars", "Gold Coins / Bars"], ["silver", "Silver"],
    ["rent_deposits_paid", "Rent Deposits Paid"],
    ["crypto", "Crypto"],
    ["sukanya_samriddhi", "Sukanya Samriddhi"],
    ["insurance_policies_cv", "Insurance Policy Cash Value"],
    ["gratuity", "Gratuity (accrued)"],
    ["inheritance", "Inheritance"],
    ["other_financial", "Other Financial"],
  ]},
  { key: "investments", label: "Investment Assets", items: [
    ["investment_real_estate", "Investment Real Estate"],
    ["land", "Land"], ["other_fixed", "Other Fixed Assets"],
  ]},
];

const LIABILITY_ITEMS = [
  ["housing_loan", "Housing Loan"], ["auto_loan", "Auto Loan"],
  ["mortgage_loan", "Mortgage Loan"], ["personal_loan", "Personal Loan"],
  ["credit_card", "Credit Card Outstanding"], ["other_liability", "Other Liability"],
];

const ALL_NUMERIC_KEYS = [
  ...ASSET_SECTIONS.flatMap((s) => s.items.map(([k]) => k)),
  ...LIABILITY_ITEMS.map(([k]) => k),
];

const blankForm = () => Object.fromEntries([...ALL_NUMERIC_KEYS.map((k) => [k, ""]), ["entry_date", firstOfMonth()], ["note", ""]]);

function MoneyField({ id, label, value, onChange }) {
  return (
    <div>
      <Label className="block text-xs text-[#5B5E5B] mb-1.5">{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8D8A] mono text-xs">₹</span>
        <Input data-testid={`nw-${id}`} type="number" min="0" placeholder="0" value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 pl-7 rounded-xl border-[#E5E2DA] bg-white focus-visible:ring-[#1A3626]/20 focus-visible:ring-2 text-sm"/>
      </div>
    </div>
  );
}

function DeltaPill({ delta }) {
  if (!delta && delta !== 0) return null;
  const Icon = delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Minus;
  const cls = delta > 0 ? "bg-[#3A5C45]/12 text-[#3A5C45]" : delta < 0 ? "bg-[#9E473D]/10 text-[#9E473D]" : "bg-[#F4F2EC] text-[#5B5E5B]";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cls}`}>
      <Icon className="h-3 w-3"/> ₹ {fmtINR(Math.abs(delta))}
    </span>
  );
}

export default function NetWorthTracker() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    let mounted = true;
    (async () => {
      try { const { data } = await api.get("/net-worth"); if (mounted) setEntries(data); }
      catch (err) { console.error("net-worth fetch failed", err); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [authLoading, user, navigate]);

  const sorted = useMemo(() => [...entries].sort((a, b) => a.entry_date.localeCompare(b.entry_date)), [entries]);
  const latest = sorted[sorted.length - 1];
  const previous = sorted[sorted.length - 2];
  const change = latest && previous ? latest.net_worth - previous.net_worth : null;

  const chartData = sorted.map((e) => ({
    date: new Date(e.entry_date).toLocaleDateString("en-IN", { month: "short", year: "2-digit" }),
    netWorth: e.net_worth, assets: e.total_assets, liabilities: e.total_liabilities,
  }));

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.entry_date) { toast.error("Pick a date for this entry"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      ALL_NUMERIC_KEYS.forEach((k) => { payload[k] = payload[k] === "" ? 0 : Number(payload[k]); });
      const prevPeak = entries.reduce((m, e) => Math.max(m, Number(e.net_worth || 0)), 0);
      const { data } = await api.post("/net-worth", payload);
      setEntries((prev) => {
        const without = prev.filter((e) => e.entry_date !== data.entry_date);
        return [...without, data];
      });
      setForm(blankForm());
      toast.success("Net worth entry saved");
      // Celebrate upward moves & milestone crossings
      const nw = Number(data.net_worth || 0);
      const crossings = [10_000_000, 5_000_000, 1_000_000];
      const crossed = crossings.find((t) => nw >= t && prevPeak < t);
      if (crossed) {
        fireConfetti({ particleCount: 160, spread: 100 });
        const label = crossed === 10_000_000 ? "₹1 Crore" : crossed === 5_000_000 ? "₹50 Lakh" : "₹10 Lakh";
        toast.success(`You just crossed ${label} in net worth!`, { duration: 5000 });
      } else if (nw > prevPeak && prevPeak > 0) {
        fireConfetti({ particleCount: 60, spread: 55 });
      }
    } catch (err) {
      console.error("net-worth save failed", err);
      toast.error("Couldn't save — please retry");
    } finally { setSaving(false); }
  };

  const remove = async (entry_date) => {
    try {
      await api.delete(`/net-worth/${entry_date}`);
      setEntries((prev) => prev.filter((e) => e.entry_date !== entry_date));
      toast.success("Entry removed");
    } catch (err) {
      console.error("net-worth delete failed", err);
      toast.error("Couldn't delete");
    }
  };

  if (authLoading || loading) return <div className="min-h-screen flex items-center justify-center text-[#5B5E5B]">Loading…</div>;

  return (
    <div className="min-h-screen page-tinted-soft lg:flex">
      <ClientSidebar user={user} onLogout={async () => { await logout(); navigate("/"); }}/>
      <main className="flex-1 max-w-6xl mx-auto px-5 sm:px-6 md:px-12 py-8 md:py-12">
        <div className="mb-8 md:mb-10">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Monthly tracker</span>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight mt-3 flex items-center gap-3">
            <TrendingUp className="h-8 w-8 md:h-9 md:w-9 text-[#1A3626]"/> Net Worth Tracker
          </h1>
          <p className="text-[#5B5E5B] mt-3 max-w-xl text-sm md:text-base">Drop in a fresh entry on the 1st of every month. Watch your wealth curve bend upward — same sections as your master datasheet.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Current</div>
            <div className="mono text-2xl mt-2 text-[#1A3626]">₹ {fmtINR(latest?.net_worth || 0)}</div>
            {change !== null && <div className="mt-2"><DeltaPill delta={change}/></div>}
            {change !== null && <div className="text-[10px] text-[#8A8D8A] mt-1">vs last entry</div>}
          </div>
          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Assets</div>
            <div className="mono text-2xl mt-2">₹ {fmtINR(latest?.total_assets || 0)}</div>
          </div>
          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Liabilities</div>
            <div className="mono text-2xl mt-2 text-[#9E473D]">₹ {fmtINR(latest?.total_liabilities || 0)}</div>
          </div>
          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Entries logged</div>
            <div className="mono text-2xl mt-2">{sorted.length}</div>
          </div>
        </div>

        {chartData.length >= 2 && (
          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-5 md:p-7 mb-8">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A] mb-1">Your trajectory</div>
            <div className="font-heading text-xl mb-4">Net worth over time</div>
            <div className="h-[240px] md:h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid stroke="#F4F2EC" vertical={false}/>
                  <XAxis dataKey="date" tick={{ fill: "#5B5E5B", fontSize: 11 }} axisLine={false} tickLine={false}/>
                  <YAxis tick={{ fill: "#8A8D8A", fontSize: 11 }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => v >= 1e7 ? `₹${(v/1e7).toFixed(1)}Cr` : v >= 1e5 ? `₹${(v/1e5).toFixed(1)}L` : `₹${(v/1000).toFixed(0)}k`}/>
                  <Tooltip formatter={(v) => `₹ ${fmtINR(v)}`} cursor={{ stroke: "#E5E2DA" }}/>
                  <Line type="monotone" dataKey="netWorth" stroke="#1A3626" strokeWidth={2.5} dot={{ fill: "#C97B2A", r: 5 }} activeDot={{ r: 7 }}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-[#E5E2DA] bg-white p-5 md:p-7 mb-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A] mb-1">Add this month&apos;s entry</div>
          <div className="font-heading text-xl mb-5">Where do you stand on the 1st?</div>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div>
              <Label className="block text-sm font-medium text-[#1C1D1C] mb-2">Entry date</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A8D8A]"/>
                <Input data-testid="nw-date" type="date" value={form.entry_date} max={today()}
                  onChange={(e) => setField("entry_date", e.target.value)}
                  className="h-11 pl-10 rounded-xl border-[#E5E2DA] bg-white focus-visible:ring-[#1A3626]/20 focus-visible:ring-2"/>
              </div>
            </div>
          </div>

          {ASSET_SECTIONS.map(({ key, label, items }) => (
            <div key={key} className="mb-7">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A3626] mb-3">{label}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                {items.map(([k, lbl]) => <MoneyField key={k} id={k} label={lbl} value={form[k]} onChange={(v) => setField(k, v)}/>)}
              </div>
            </div>
          ))}

          <div className="mb-7">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#9E473D] mb-3">Liabilities</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
              {LIABILITY_ITEMS.map(([k, lbl]) => <MoneyField key={k} id={k} label={lbl} value={form[k]} onChange={(v) => setField(k, v)}/>)}
            </div>
          </div>

          <div className="mb-5">
            <Label className="block text-sm font-medium text-[#1C1D1C] mb-2">Note (optional)</Label>
            <Textarea data-testid="nw-note" value={form.note} onChange={(e) => setField("note", e.target.value)}
              placeholder="What changed this month?" className="rounded-xl border-[#E5E2DA] bg-white"/>
          </div>
          <Button data-testid="nw-save" onClick={save} disabled={saving} className="rounded-full bg-[#1A3626] hover:bg-[#2C4C3B] text-white h-11 px-7 w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-2"/> {saving ? "Saving…" : "Save entry"}
          </Button>
        </div>

        {sorted.length > 0 && (
          <div className="space-y-4">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Monthly entries</div>
            {[...sorted].reverse().map((e, idx, arr) => {
              const prev = arr[idx + 1];
              const monthLabel = new Date(e.entry_date).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
              const delta = prev ? e.net_worth - prev.net_worth : null;
              return (
                <div key={e.entry_date} className="rounded-2xl border border-[#E5E2DA] bg-white p-5 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <div>
                      <div className="font-heading text-lg">{monthLabel}</div>
                      <div className="text-xs text-[#8A8D8A]">{e.entry_date}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      {delta !== null && <DeltaPill delta={delta}/>}
                      <button data-testid={`nw-rm-${e.entry_date}`} onClick={() => remove(e.entry_date)} className="text-[#9E473D] hover:text-[#7A3A33]" aria-label="Delete entry">
                        <Trash2 className="h-4 w-4"/>
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-[#8B9D8B] font-bold">Assets</div>
                      <div className="mono text-base mt-1">₹ {fmtINR(e.total_assets)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-[#8B9D8B] font-bold">Liabilities</div>
                      <div className="mono text-base mt-1 text-[#9E473D]">₹ {fmtINR(e.total_liabilities)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-[#8B9D8B] font-bold">Net worth</div>
                      <div className="mono text-base mt-1 text-[#1A3626] font-medium">₹ {fmtINR(e.net_worth)}</div>
                    </div>
                  </div>
                  {e.note && <div className="text-sm text-[#5B5E5B] mt-3 pt-3 border-t border-[#E5E2DA]/60 italic">{e.note}</div>}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
